import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Benefit cards — short, punchy headline + bullet snippets affiliates
// can drop into IG/TikTok carousels or graphics.
//
// API contract (per BE handoff 2026-05-18):
//   GET /affiliate/content/benefit-cards →
//     { items: [{ id, title, body, programs:[],
//                 metadata: { headline, bullets:[] } }] }
//
// Render headline + bullets from `metadata` — `body` is a markdown
// fallback for cards that don't ship structured metadata.
export default function AssetsBenefitCards() {
  const [cards, setCards] = useState(null);
  const [program, setProgram] = useProgramFromQuery();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/benefit-cards", { auth: true });
        const items = res?.items || res?.cards || [];
        if (!cancelled) setCards(items);
      } catch {
        if (!cancelled) setCards([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!cards) return [];
    const set = new Set();
    cards.forEach((c) => (c.programs || []).forEach((p) => set.add(p)));
    return Array.from(set);
  }, [cards]);

  const visible = useMemo(() => {
    if (!cards) return [];
    return cards.filter((c) =>
      program === "all" || (c.programs || []).includes(program),
    );
  }, [cards, program]);

  if (cards === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="BENEFIT" italic="cards." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Headline + bullet snippets for carousels, reels, and graphics. Copy the whole card or just the bullets you need.
      </p>
      {programs.length > 1 && (
        <div style={{ marginTop: 24 }}>
          <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="benefit-program" />
        </div>
      )}
      <div data-testid="benefit-cards-list" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {visible.length === 0 ? (
          <div data-testid="benefit-cards-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No benefit cards match these filters.
          </div>
        ) : (
          visible.map((c) => {
            const headline = c.metadata?.headline || c.title;
            const bullets = c.metadata?.bullets || [];
            const fallbackBody = !bullets.length ? c.body : null;
            const copyText = [headline, "", ...bullets.map((b) => `· ${b}`)].filter(Boolean).join("\n");
            return (
              <article
                key={c.id || c._id}
                data-testid={`benefit-card-${c.id || c._id}`}
                style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 22, display: "flex", flexDirection: "column", gap: 12 }}
              >
                <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                  {headline}
                </h3>
                {bullets.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6, color: C.ink }}>
                    {bullets.map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
                  </ul>
                ) : fallbackBody ? (
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: C.ink, whiteSpace: "pre-wrap" }}>{fallbackBody}</p>
                ) : null}
                <div style={{ flex: 1 }} />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <CopyButton text={copyText} testId={`benefit-copy-${c.id || c._id}`} label="COPY CARD" />
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}
